import { Badge, HStack, Switch, Text, VStack } from "@chakra-ui/react";
import { useFormContext, useWatch } from "react-hook-form";
import type { CreateCampaignFlowValues } from "../../forms/campaignFlowForm";

/** One org-defined targeting strategy row when Smart Targeting is off (toggle in this section). */
export type ManualTargetingStrategyOption = {
  id: string;
  name: string;
  description: string;
  recommended: boolean;
};

type StrategiesSectionProps = {
  options: ManualTargetingStrategyOption[];
  /** Fired when the user toggles a strategy (not programmatic setValue from defaults). */
  onUserChangedEnabledStrategyIds?: () => void;
};

export default function StrategiesSection({
  options,
  onUserChangedEnabledStrategyIds,
}: StrategiesSectionProps) {
  const { setValue, trigger } = useFormContext<CreateCampaignFlowValues>();
  const enabledIds =
    useWatch<CreateCampaignFlowValues, "enabled_strategy_ids">({
      name: "enabled_strategy_ids",
    }) ?? [];

  if (options.length === 0) return null;

  return (
    <VStack align="stretch" spacing={4} w="full">
      {options.map((s) => {
        const checked = enabledIds.includes(s.id);
        return (
          <HStack
            key={s.id}
            justify="space-between"
            align="flex-start"
            w="full"
            py={2}
          >
            <VStack align="flex-start" spacing={1} flex={1} pr={4}>
              <HStack flexWrap="wrap" gap={2}>
                <Text fontSize="md" fontWeight="semibold">
                  Include {s.name}
                </Text>
                {s.recommended ? (
                  <Badge
                    colorScheme="green"
                    textTransform="none"
                    fontWeight="semibold"
                  >
                    Recommended
                  </Badge>
                ) : null}
              </HStack>
              <Text fontSize="sm" color="gray.600">
                {s.description}
              </Text>
            </VStack>
            <Switch
              size="lg"
              aria-label={`Include ${s.name}`}
              isChecked={checked}
              onChange={(e) => {
                const next = e.target.checked
                  ? Array.from(new Set(enabledIds.concat(s.id)))
                  : enabledIds.filter((id) => id !== s.id);
                onUserChangedEnabledStrategyIds?.();
                setValue("enabled_strategy_ids", next, {
                  shouldDirty: true,
                  shouldValidate: true,
                });
                void trigger("custom_audiences");
              }}
            />
          </HStack>
        );
      })}
    </VStack>
  );
}
